/* Study Design Advisor — 3-question wizard that recommends which of the
   4 MET modules to launch based on user's trial structure.
*/
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';

const QUESTIONS = [
  {
    id: 'locations',
    q: 'How many locations does your trial span?',
    options: [
      { v: 'one', l: 'One location' },
      { v: 'multi', l: 'Multiple locations' },
    ],
  },
  {
    id: 'years',
    q: 'How many years of data do you have?',
    options: [
      { v: 'one', l: 'One year' },
      { v: 'multi', l: 'Multiple years' },
    ],
  },
  {
    id: 'sameLoc',
    q: 'If multiple years — same locations each year?',
    options: [
      { v: 'same', l: 'Same locations' },
      { v: 'diff', l: 'Different locations' },
      { v: 'na', l: 'Not applicable' },
    ],
  },
];

function recommend({ locations, years, sameLoc }) {
  // Decision tree
  if (locations === 'multi' && years === 'one') {
    return {
      module: 'pooled-anova',
      label: 'Pooled ANOVA',
      reason: 'Multi-location single-year: combine locations in one pooled ANOVA with Bartlett homogeneity check.',
      structure: 'locations_one_year',
    };
  }
  if (locations === 'one' && years === 'multi') {
    return {
      module: 'genotype-year',
      label: 'Genotype × Year Analysis',
      reason: 'Single location over multiple years: temporal-stability analysis with realized gain and year decomposition.',
    };
  }
  if (locations === 'multi' && years === 'multi' && sameLoc === 'same') {
    return {
      module: 'full-met',
      label: 'Full MET Analysis',
      reason: 'Multi-location × multi-year with balanced locations: full 3-way ANOVA with mega-environment delineation and breeding zone recommendation.',
    };
  }
  if (locations === 'multi' && years === 'multi' && sameLoc === 'diff') {
    return {
      module: 'pooled-anova',
      label: 'Pooled ANOVA (Locations × Years)',
      reason: 'Multi-location × multi-year with shifting locations: treat each Location_Year as a composite environment in a pooled ANOVA.',
      structure: 'locations_and_years',
    };
  }
  if (locations === 'one' && years === 'one') {
    return {
      module: null,
      label: 'Single environment — use RBD or CRD',
      reason: 'With one location and one year you do not need a MET module — run a standard RBD/CRD from the Experimental Designs section.',
    };
  }
  return null;
}

export default function StudyDesignAdvisor() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [answers, setAnswers] = useState({ locations: null, years: null, sameLoc: null });

  const allAnswered = answers.locations && answers.years && (
    (answers.years === 'one') || answers.sameLoc
  );
  const rec = allAnswered ? recommend(answers) : null;

  const launch = () => {
    if (!rec?.module) return;
    const params = new URLSearchParams({ section: 'stability', design: rec.module });
    if (rec.structure) {
      sessionStorage.setItem('bk_advisor_structure', rec.structure);
    }
    navigate(`/biometrical-analysis?${params.toString()}`);
    // Force a refresh of current page with new query
    window.history.pushState({}, '', `/biometrical-analysis?${params.toString()}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  return (
    <div className="rounded-xl overflow-hidden mb-4" style={{ border: '1px solid #86EFAC', background: 'linear-gradient(135deg, #F0FDF4, #ECFDF5)' }} data-testid="study-design-advisor">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 transition hover:bg-emerald-100"
        data-testid="advisor-toggle"
      >
        <div className="flex items-center gap-2">
          <Sparkles size={14} style={{ color: '#059669' }} />
          <span className="font-body" style={{ fontSize: 13, fontWeight: 700, color: '#064E3B', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Study Design Advisor
          </span>
          <span className="font-body" style={{ fontSize: 12, color: '#065F46' }}>
            Not sure which analysis? Answer 3 questions.
          </span>
        </div>
        {open ? <ChevronUp size={16} style={{ color: '#059669' }} /> : <ChevronDown size={16} style={{ color: '#059669' }} />}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3 fade-in">
          {QUESTIONS.map((question, qi) => {
            // Skip Q3 if years='one'
            if (question.id === 'sameLoc' && answers.years === 'one') return null;
            return (
              <div key={question.id} data-testid={`advisor-q-${question.id}`}>
                <div className="font-body mb-1.5" style={{ fontSize: 13, fontWeight: 600, color: '#064E3B' }}>
                  {qi + 1}. {question.q}
                </div>
                <div className="flex flex-wrap gap-2">
                  {question.options.map(opt => (
                    <button
                      key={opt.v}
                      onClick={() => setAnswers(a => ({ ...a, [question.id]: opt.v }))}
                      className="px-3 py-1.5 rounded-lg font-body transition-all"
                      style={{
                        fontSize: 12, fontWeight: 600,
                        border: `2px solid ${answers[question.id] === opt.v ? '#059669' : '#D1D5DB'}`,
                        background: answers[question.id] === opt.v ? '#059669' : 'white',
                        color: answers[question.id] === opt.v ? 'white' : '#374151',
                      }}
                      data-testid={`advisor-a-${question.id}-${opt.v}`}
                    >
                      {opt.l}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}

          {rec && (
            <div className="rounded-lg p-3 mt-3" style={{ background: 'white', border: '2px solid #059669' }} data-testid="advisor-recommendation">
              <div className="font-body" style={{ fontSize: 11, fontWeight: 700, color: '#059669', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Recommended
              </div>
              <div className="font-body mt-1" style={{ fontSize: 16, fontWeight: 700, color: '#064E3B' }}>
                {rec.label}
              </div>
              <p className="font-body mt-1" style={{ fontSize: 12, color: '#374151' }}>
                {rec.reason}
              </p>
              {rec.module && (
                <button
                  onClick={launch}
                  className="mt-3 flex items-center gap-2 px-4 py-2 rounded-lg font-body btn-press transition"
                  style={{ fontSize: 13, fontWeight: 700, background: '#059669', color: 'white', border: 'none' }}
                  data-testid="advisor-launch-btn"
                >
                  Launch {rec.label} <ArrowRight size={14} />
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
